import { Signal } from "@engine";
import type { Shape } from "../level/types";
import type { DifficultyId } from "../config/difficulty";

export type HudState =
  | "loading"
  | "menu"
  | "game"
  | "paused"
  | "success"
  | "dead"
  | "stats"
  | "story-select";
export type PopupKind = "accent" | "warm" | "hot" | "danger";
export type DeadTitleKind = "death" | "victory";
export type MenuMode = "story" | "endless" | "daily";
export type MenuExtraAction = "stats";
export type PauseAction = "resume" | "restart" | "menu";

const HUD_THROTTLE_MS = 80;
const POPUP_POOL_SIZE = 8;
const POPUP_LIFETIME_MS = 800;
const NUMBER_LERP_LAMBDA = 12;

interface PopupSlot {
  el: HTMLElement;
  busy: boolean;
  releaseAt: number;
}

export class HUD {
  readonly onMenuAction = new Signal<MenuMode>();
  readonly onMenuExtra = new Signal<MenuExtraAction>();
  readonly onPauseAction = new Signal<PauseAction>();
  // Stats screen action signals: clicking Back fires "back"; clicking
  // a tab fires that tab id. Cosmetics card clicks are handled by the
  // StatsScreen module directly (it owns the dynamic markup).
  readonly onStatsAction = new Signal<"back">();
  // Story Level-Select sub-screen Back button. Card clicks themselves
  // are handled by LevelSelectScreen.onSelect (it owns the dynamic
  // cards), mirroring the StatsScreen / cosmetics pattern.
  readonly onStoryAction = new Signal<"back">();
  // Fired when the player picks a difficulty pill in the menu. The
  // game routes this to DifficultyService.setLevel; the persistence
  // is handled there. setDifficultyActive paints the pill on apply.
  readonly onDifficulty = new Signal<DifficultyId>();

  private root: HTMLElement;
  private screens: Record<HudState, HTMLElement>;
  private loadingBar: HTMLElement;
  private shapeEl: HTMLElement;
  private levelEl: HTMLElement;
  private distanceEl: HTMLElement;
  private comboEl: HTMLElement;
  private scoreEl: HTMLElement;
  private finalDistanceEl: HTMLElement;
  private finalScoreEl: HTMLElement;
  private deadTitleEl: HTMLElement;
  private successLevelEl: HTMLElement;
  private successDistanceEl: HTMLElement;
  private successScoreEl: HTMLElement;
  private flashEl: HTMLElement;
  private popupRoot: HTMLElement;
  private menuEndlessSubEl: HTMLElement;
  private menuDailySubEl: HTMLElement;
  private tutorialEl: HTMLElement;
  private shieldEl: HTMLElement;
  private slowmoEl: HTMLElement;
  private magnetEl: HTMLElement;
  private doubleEl: HTMLElement;
  private beatDotEl: HTMLElement;
  private frenzyEl: HTMLElement;
  private bossEl: HTMLElement;
  private mirrorEl: HTMLElement;
  private dailyModEl: HTMLElement;
  private difficultyBadgeEl: HTMLElement;
  // Every container with class .menu-difficulty (currently the menu
  // pill row and the story-select pill row). One pattern, one binding,
  // one paint pass — so any future screen that drops a third copy of
  // the row picks up the same behavior for free.
  private difficultyRowEls: HTMLElement[];
  private beatDotResetTimer: number | null = null;
  private popups: PopupSlot[] = [];
  private state: HudState = "loading";
  private tutorialDismissTimer: number | null = null;

  private displayedDistance = 0;
  private displayedScore = 0;
  private lastShownDistance = -1;
  private lastShownScore = -1;
  private lastDistanceWriteAt = 0;
  private lastScoreWriteAt = 0;

  constructor(root?: HTMLElement) {
    const r = root ?? (document.getElementById("hud") as HTMLElement | null);
    if (!r) throw new Error("HUD: #hud not found");
    this.root = r;
    this.screens = {
      loading: this.required("hud-loading"),
      menu: this.required("hud-menu"),
      game: this.required("hud-game"),
      paused: this.required("hud-pause"),
      success: this.required("hud-success"),
      dead: this.required("hud-dead"),
      stats: this.required("hud-stats"),
      "story-select": this.required("hud-story-select"),
    };
    this.loadingBar = this.required("hud-loading-bar");
    this.shapeEl = this.required("hud-shape");
    this.levelEl = this.required("hud-level");
    this.distanceEl = this.required("hud-distance");
    this.comboEl = this.required("hud-combo");
    this.scoreEl = this.required("hud-score");
    this.finalDistanceEl = this.required("hud-final-distance");
    this.finalScoreEl = this.required("hud-final-score");
    this.deadTitleEl = this.required("hud-dead-title");
    this.successLevelEl = this.required("hud-success-level");
    this.successDistanceEl = this.required("hud-success-distance");
    this.successScoreEl = this.required("hud-success-score");
    this.flashEl = this.required("hud-flash");
    this.popupRoot = this.required("hud-popups");
    this.menuEndlessSubEl = this.required("hud-menu-endless-sub");
    this.menuDailySubEl = this.required("hud-menu-daily-sub");
    this.tutorialEl = this.required("hud-tutorial");
    this.shieldEl = this.required("hud-shield");
    this.slowmoEl = this.required("hud-slowmo");
    this.magnetEl = this.required("hud-magnet");
    this.doubleEl = this.required("hud-double");
    this.beatDotEl = this.required("hud-beat-dot");
    this.frenzyEl = this.required("hud-frenzy");
    this.bossEl = this.required("hud-boss");
    this.mirrorEl = this.required("hud-mirror");
    this.dailyModEl = this.required("hud-daily-modifier");
    this.difficultyBadgeEl = this.required("hud-difficulty");
    this.difficultyRowEls = Array.from(
      document.querySelectorAll<HTMLElement>(".menu-difficulty")
    );
    if (this.difficultyRowEls.length === 0) {
      throw new Error("HUD: no .menu-difficulty rows found");
    }
    this.initPopupPool();
    this.bindMenuButtons();
    this.bindPauseButtons();
    this.bindStatsButtons();
    this.bindStoryButtons();
    this.bindDifficultyButtons();
    this.setState("loading");
  }

  setState(state: HudState): void {
    this.state = state;
    this.root.dataset["state"] = state;
    for (const [name, el] of Object.entries(this.screens)) {
      el.hidden = name !== state;
    }
  }

  getState(): HudState {
    return this.state;
  }

  setLoading(progress01: number): void {
    const pct = Math.max(0, Math.min(1, progress01)) * 100;
    this.loadingBar.style.width = `${pct.toFixed(1)}%`;
  }

  setShape(shape: Shape): void {
    this.shapeEl.textContent = shape.toUpperCase();
    this.shapeEl.dataset["shape"] = shape;
  }

  setLevel(levelNumber: number): void {
    this.levelEl.textContent = `LV ${levelNumber}`;
  }

  setModeLabel(label: string): void {
    this.levelEl.textContent = label;
  }

  setSuccess(
    levelNumber: number,
    distance: number,
    score: number,
    isNewBest = false
  ): void {
    this.successLevelEl.textContent = String(levelNumber);
    this.successLevelEl.classList.remove("pop-in", "new-best");
    void this.successLevelEl.offsetWidth;
    this.successLevelEl.classList.add("pop-in");
    if (isNewBest) this.successLevelEl.classList.add("new-best");
    this.successDistanceEl.textContent = `${Math.floor(distance)} M`;
    this.successScoreEl.textContent = score.toLocaleString();
  }

  setDeadTitle(
    text: string,
    kind: DeadTitleKind = "death",
    isNewBest = false
  ): void {
    this.deadTitleEl.textContent = text;
    this.deadTitleEl.classList.remove("dead", "victory", "new-best");
    this.deadTitleEl.classList.add(kind === "victory" ? "victory" : "dead");
    if (isNewBest) this.deadTitleEl.classList.add("new-best");
  }

  // Per-frame: tweens the displayed distance toward the real value and
  // writes the rounded number to the DOM at most every HUD_THROTTLE_MS.
  updateDistanceTween(realDistance: number, dt: number): void {
    const k = 1 - Math.exp(-NUMBER_LERP_LAMBDA * dt);
    this.displayedDistance += (realDistance - this.displayedDistance) * k;
    const now = performance.now();
    if (now - this.lastDistanceWriteAt < HUD_THROTTLE_MS) return;
    this.lastDistanceWriteAt = now;
    const rounded = Math.floor(this.displayedDistance);
    if (rounded === this.lastShownDistance) return;
    this.lastShownDistance = rounded;
    this.distanceEl.textContent = `${rounded} M`;
  }

  setScore(realScore: number, dt = 0): void {
    if (dt > 0) {
      const k = 1 - Math.exp(-NUMBER_LERP_LAMBDA * dt);
      this.displayedScore += (realScore - this.displayedScore) * k;
    } else {
      this.displayedScore = realScore;
    }
    const now = performance.now();
    if (now - this.lastScoreWriteAt < HUD_THROTTLE_MS) return;
    this.lastScoreWriteAt = now;
    const rounded = Math.floor(this.displayedScore);
    if (rounded === this.lastShownScore) return;
    this.lastShownScore = rounded;
    this.scoreEl.textContent = rounded.toLocaleString();
  }

  setCombo(combo: number, multiplier: number): void {
    if (combo <= 0 || multiplier <= 1) {
      this.comboEl.hidden = true;
      this.comboEl.classList.remove("pop");
      return;
    }
    this.comboEl.hidden = false;
    this.comboEl.textContent = `x${multiplier}`;
    this.comboEl.classList.remove("pop");
    void this.comboEl.offsetWidth;
    this.comboEl.classList.add("pop");
  }

  setFinalDistance(meters: number): void {
    this.finalDistanceEl.textContent = `${Math.floor(meters)} M`;
  }

  setFinalScore(score: number): void {
    this.finalScoreEl.textContent = score.toLocaleString();
  }

  spawnPopup(text: string, kind: PopupKind = "accent"): void {
    const slot = this.popups.find((p) => !p.busy);
    if (!slot) return;
    slot.busy = true;
    slot.releaseAt = performance.now() + POPUP_LIFETIME_MS;
    slot.el.className = `popup kind-${kind}`;
    slot.el.textContent = text;
    slot.el.classList.remove("rise");
    void slot.el.offsetWidth;
    slot.el.classList.add("rise");
    window.setTimeout(() => {
      slot.busy = false;
      slot.el.classList.remove("rise");
      slot.el.textContent = "";
    }, POPUP_LIFETIME_MS);
  }

  flash(kind: "death" | "gate" = "death", color?: string): void {
    this.flashEl.classList.remove("flash", "flash-gate");
    if (color) this.flashEl.style.backgroundColor = color;
    else this.flashEl.style.removeProperty("background-color");
    void this.flashEl.offsetWidth;
    this.flashEl.classList.add("flash");
    if (kind === "gate") this.flashEl.classList.add("flash-gate");
    window.setTimeout(() => {
      this.flashEl.classList.remove("flash", "flash-gate");
    }, 220);
  }

  setMenuBests(endlessBest: number | null, dailyLabel: string): void {
    this.menuEndlessSubEl.textContent =
      endlessBest != null ? `Best: ${endlessBest.toLocaleString()}` : "Best: -";
    this.menuDailySubEl.textContent = dailyLabel;
  }

  setShield(active: boolean): void {
    this.shieldEl.hidden = !active;
  }

  // Pass `null` to hide the chip, otherwise pass the seconds remaining.
  // We re-write the label every frame the timer is active because the
  // pickup manager updates on real time so frequent reads keep the
  // countdown precise.
  setSlowMoTimer(secondsRemaining: number | null): void {
    if (secondsRemaining == null || secondsRemaining <= 0) {
      this.slowmoEl.hidden = true;
      return;
    }
    this.slowmoEl.hidden = false;
    this.slowmoEl.textContent = `SLOW MO ${secondsRemaining.toFixed(1)}S`;
  }

  setMagnetTimer(secondsRemaining: number | null): void {
    if (secondsRemaining == null || secondsRemaining <= 0) {
      this.magnetEl.hidden = true;
      return;
    }
    this.magnetEl.hidden = false;
    this.magnetEl.textContent = `MAGNET ${secondsRemaining.toFixed(1)}S`;
  }

  setDoubleTimer(secondsRemaining: number | null): void {
    if (secondsRemaining == null || secondsRemaining <= 0) {
      this.doubleEl.hidden = true;
      return;
    }
    this.doubleEl.hidden = false;
    this.doubleEl.textContent = `2x SCORE ${secondsRemaining.toFixed(1)}S`;
  }

  showTutorial(): void {
    this.tutorialEl.classList.remove("fade-out");
    this.tutorialEl.hidden = false;
  }

  hideTutorial(): void {
    if (this.tutorialEl.hidden) return;
    if (this.tutorialDismissTimer !== null) {
      window.clearTimeout(this.tutorialDismissTimer);
    }
    this.tutorialEl.classList.add("fade-out");
    this.tutorialDismissTimer = window.setTimeout(() => {
      this.tutorialEl.hidden = true;
      this.tutorialEl.classList.remove("fade-out");
      this.tutorialDismissTimer = null;
    }, 360);
  }

  // Pulse the rhythm dot. MorphHexGame schedules a setTimeout aligned
  // to the AudioContext kick/snare time so the dot lands AS the
  // listener hears the hit, not when the scheduler queued it (~150ms
  // earlier). Cheap CSS class toggle; the previous pulse is cancelled
  // if a new one comes in fast (drum rolls).
  pulseBeat(kind: "kick" | "snare"): void {
    if (this.beatDotResetTimer !== null) {
      window.clearTimeout(this.beatDotResetTimer);
      this.beatDotResetTimer = null;
    }
    this.beatDotEl.classList.remove("pulse-kick", "pulse-snare");
    void this.beatDotEl.offsetWidth;
    this.beatDotEl.classList.add(
      kind === "kick" ? "pulse-kick" : "pulse-snare"
    );
    this.beatDotResetTimer = window.setTimeout(() => {
      this.beatDotEl.classList.remove("pulse-kick", "pulse-snare");
      this.beatDotResetTimer = null;
    }, 130);
  }

  // FrenzyMode flips this on activate and off on deactivate. The CSS
  // animation handles the visual pulse for the duration; we just
  // toggle visibility.
  setFrenzy(active: boolean): void {
    this.frenzyEl.hidden = !active;
  }

  // Pass a name to reveal the boss banner (LevelStreamer.onBossStart),
  // null to hide it (LevelStreamer.onBossCleared or any state reset).
  setBossBanner(name: string | null): void {
    if (name === null || name.length === 0) {
      this.bossEl.hidden = true;
      this.bossEl.textContent = "";
      return;
    }
    this.bossEl.textContent = name;
    this.bossEl.hidden = false;
    // Restart the entrance animation so the player notices each new
    // boss even if they're chained back-to-back in the same run.
    this.bossEl.style.animation = "none";
    void this.bossEl.offsetWidth;
    this.bossEl.style.animation = "";
  }

  // Mirror trick segment indicator. The chip lives in the stats row
  // with a CSS-driven flip animation so it visually communicates
  // "rotation is inverted right now" at a glance.
  setMirrorIndicator(active: boolean): void {
    this.mirrorEl.hidden = !active;
  }

  // Show the active daily-challenge modifier as a top-of-screen badge.
  // Pass null/empty string to hide. Wires into MorphHexGame.startGame
  // so the player sees what's different about today's run before
  // they're more than a few seconds in.
  setDailyModifierBanner(label: string | null): void {
    if (!label) {
      this.dailyModEl.hidden = true;
      this.dailyModEl.textContent = "";
      return;
    }
    this.dailyModEl.textContent = label;
    this.dailyModEl.hidden = false;
  }

  private bindMenuButtons(): void {
    const menu = this.screens.menu;
    menu.addEventListener("click", (ev) => {
      const target = (ev.target as HTMLElement).closest(
        "button[data-act]"
      ) as HTMLElement | null;
      if (!target) return;
      const act = target.dataset["act"];
      if (act === "story" || act === "endless" || act === "daily") {
        this.onMenuAction.emit(act);
      } else if (act === "stats") {
        this.onMenuExtra.emit("stats");
      }
    });
  }

  // Difficulty pill row click handler. Emits onDifficulty with the
  // pill's data-diff attribute so the game layer can update the
  // service + repaint the active pill via setDifficultyActive.
  private bindDifficultyButtons(): void {
    for (const row of this.difficultyRowEls) {
      row.addEventListener("click", (ev) => {
        const target = (ev.target as HTMLElement).closest(
          "button[data-diff]"
        ) as HTMLElement | null;
        if (!target) return;
        const diff = target.dataset["diff"];
        if (diff === "easy" || diff === "normal" || diff === "hard") {
          this.onDifficulty.emit(diff);
        }
      });
    }
  }

  // Repaint the active pill in EVERY .menu-difficulty row and update
  // the in-game DIFF badge. Called by MorphHexGame on init (to reflect
  // the persisted selection) and after every onDifficulty round-trip.
  setDifficultyActive(id: DifficultyId): void {
    for (const row of this.difficultyRowEls) {
      for (const btn of row.querySelectorAll("button.diff")) {
        const b = btn as HTMLElement;
        b.classList.toggle("active", b.dataset["diff"] === id);
      }
    }
    if (id === "normal") {
      this.difficultyBadgeEl.hidden = true;
      this.difficultyBadgeEl.textContent = "";
      return;
    }
    this.difficultyBadgeEl.hidden = false;
    this.difficultyBadgeEl.classList.remove("easy", "hard");
    this.difficultyBadgeEl.classList.add(id);
    this.difficultyBadgeEl.textContent = `DIFF ${id.toUpperCase()}`;
  }

  private bindStatsButtons(): void {
    const stats = this.screens.stats;
    stats.addEventListener("click", (ev) => {
      const target = (ev.target as HTMLElement).closest(
        "button[data-act]"
      ) as HTMLElement | null;
      if (!target) return;
      const act = target.dataset["act"];
      if (act === "back") {
        this.onStatsAction.emit("back");
      }
    });
  }

  // Back button on the Story Level-Select screen. Card clicks live in
  // LevelSelectScreen (it owns the dynamic markup); the difficulty
  // pill row inside the screen reuses the global onDifficulty signal
  // bound by bindDifficultyButtons() because we delegate by class.
  private bindStoryButtons(): void {
    const screen = this.screens["story-select"];
    screen.addEventListener("click", (ev) => {
      const target = (ev.target as HTMLElement).closest(
        "button[data-act]"
      ) as HTMLElement | null;
      if (!target) return;
      const act = target.dataset["act"];
      if (act === "back") {
        this.onStoryAction.emit("back");
      }
    });
  }

  private bindPauseButtons(): void {
    const pause = this.screens.paused;
    pause.addEventListener("click", (ev) => {
      const target = (ev.target as HTMLElement).closest(
        "button[data-act]"
      ) as HTMLElement | null;
      if (!target) return;
      const act = target.dataset["act"] as PauseAction | undefined;
      if (act === "resume" || act === "restart" || act === "menu") {
        this.onPauseAction.emit(act);
      }
    });
  }

  private initPopupPool(): void {
    this.popupRoot.innerHTML = "";
    for (let i = 0; i < POPUP_POOL_SIZE; i++) {
      const el = document.createElement("div");
      el.className = "popup";
      this.popupRoot.appendChild(el);
      this.popups.push({ el, busy: false, releaseAt: 0 });
    }
  }

  private required(id: string): HTMLElement {
    const el = document.getElementById(id);
    if (!el) throw new Error(`HUD: missing element #${id}`);
    return el;
  }
}
